<template>
  <Teleport to="body">
    <transition :name="transitionName">
      <div
        v-if="modelValue"
        class="fo-backdrop"
        :class="backdropClass"
        role="dialog"
        aria-modal="true"
        @keydown.esc="handleEsc"
      >
        <div
          class="fo-panel card shadow-lg"
          :class="panelClass"
          :style="[
            panelStyle,
            placement === 'bottom' && props.sheetHeight
              ? { height: sheetHeight }
              : null,
          ]"
          tabindex="-1"
          ref="panelRef"
        >
          <!-- Header -->
          <div class="card-header d-flex align-items-center">
            <h2 class="h6 mb-0">{{ title }}</h2>
            <button
              v-if="closable"
              class="btn btn-sm btn-light ms-auto"
              @click="emitClose"
              aria-label="Cerrar"
            >
              <span class="material-symbols-rounded">close</span>
            </button>
          </div>

          <!-- Body -->
          <div class="card-body">
            <slot />
          </div>

          <!-- Footer GENÉRICO -->
          <!-- Footer GENÉRICO -->
          <div
            class="card-footer d-flex justify-content-between align-items-center"
          >
            <!-- Izquierda: botón(es) extra -->
            <div class="d-flex">
              <slot name="footer-left"></slot>
            </div>

            <!-- Derecha: botones estándar -->
            <div class="d-flex gap-2">
              <slot name="footer">
                <button
                  class="btn btn-outline-secondary"
                  @click="emitClose"
                  :disabled="loading"
                >
                  {{ cancelLabel }}
                </button>
                <button
                  class="btn btn-success"
                  @click="emitConfirm"
                  :disabled="loading"
                >
                  <span
                    v-if="loading"
                    class="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  ></span>
                  {{ okLabel }}
                </button>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed, onBeforeUnmount } from "vue";

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  title: { type: String, default: "Diálogo" },
  closable: { type: Boolean, default: true },
  loading: { type: Boolean, default: false },
  okLabel: { type: String, default: "Guardar" },
  cancelLabel: { type: String, default: "Cancelar" },

  /** 'auto' (mobile=modal / desktop=drawer), 'modal' o 'drawer' */
  layout: { type: String, default: "auto" },

  placement: {
    type: String,
    default: "center", // 'center' | 'bottom' | 'top'
    validator: (v) => ["center", "bottom", "top"].includes(v),
  },

  /** ancho del drawer en px (solo drawer) */
  drawerWidth: { type: [Number, String], default: 520 },
  sheetHeight: { type: String, default: null },

  panelClass: { type: String, default: "" },
});

const emit = defineEmits(["update:modelValue", "confirm", "cancel"]);

const panelRef = ref(null);

function emitClose() {
  if (!props.closable) return;
  emit("cancel");
  emit("update:modelValue", false);
}
function emitConfirm() {
  emit("confirm"); // el hijo decide cerrar si todo OK
}
function handleEsc() {
  if (props.closable) emitClose();
}

// resuelve si es mobile con matchMedia cuando layout=auto
const isMobile = ref(false);
let mq;
if (typeof window !== "undefined") {
  mq = window.matchMedia("(max-width: 576px)");
  const apply = (m) => (isMobile.value = !!m.matches);
  apply(mq);
  mq.addEventListener?.("change", apply) || mq.addListener?.(apply);
}
onBeforeUnmount(() => {
  if (mq)
    mq.removeEventListener?.("change", () => {}) ||
      mq.removeListener?.(() => {});
});

const resolvedLayout = computed(() => {
  if (props.layout === "modal" || props.layout === "drawer")
    return props.layout;
  // auto
  return isMobile.value ? "modal" : "drawer";
});

const panelClass = computed(() => ({
  "as-modal": resolvedLayout.value === "modal",
  "as-drawer": resolvedLayout.value === "drawer",
}));

const panelStyle = computed(() => {
  if (resolvedLayout.value === "drawer") {
    const w =
      typeof props.drawerWidth === "number"
        ? `${props.drawerWidth}px`
        : String(props.drawerWidth);
    return { "--drawer-w": w };
  }
  return {};
});

/** Centrado/posición: se aplica sobre el backdrop cuando es modal */
const backdropClass = computed(() => {
  if (resolvedLayout.value !== "modal") return null;
  return {
    "place-center": props.placement === "center",
    "place-bottom": props.placement === "bottom",
    "place-top": props.placement === "top",
  };
});

const transitionName = computed(() => {
  if (resolvedLayout.value === "drawer") return "slide-right";
  // modal:
  if (props.placement === "bottom") return "slide-up";
  return "fade"; // center/top
});

// focus trap básico
function trapFocus(e) {
  if (!props.modelValue) return;
  if (e.key !== "Tab") return;
  const els = panelRef.value?.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  if (!els?.length) return;
  const first = els[0];
  const last = els[els.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

watch(
  () => props.modelValue,
  (open, wasOpen) => {
    if (open) {
      document.addEventListener("keydown", trapFocus);

      if (!wasOpen) {
        lockPageScroll();
      }
    } else {
      document.removeEventListener("keydown", trapFocus);

      if (wasOpen) {
        unlockPageScroll();
      }
    }
  },
);

onBeforeUnmount(() => {
  document.removeEventListener("keydown", trapFocus);

  if (props.modelValue) {
    unlockPageScroll();
  }
});

function lockPageScroll() {
  const actual = Number(document.body.dataset.modalLocks || "0");
  const nuevo = actual + 1;

  document.body.dataset.modalLocks = String(nuevo);

  document.body.classList.add("modal-page-locked");
  document.documentElement.classList.add("modal-page-locked");
}

function unlockPageScroll() {
  const actual = Number(document.body.dataset.modalLocks || "0");
  const nuevo = Math.max(0, actual - 1);

  document.body.dataset.modalLocks = String(nuevo);

  if (nuevo === 0) {
    document.body.classList.remove("modal-page-locked");
    document.documentElement.classList.remove("modal-page-locked");
  }
}
</script>

<style scoped>
.fo-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(33, 37, 41, 0.5);
  z-index: 1050;
  display: grid;
  padding: 5px; /* evita que toque bordes en móviles */
}

/* POSICIONAMIENTO DEL MODAL (solo layout modal) */
.place-center {
  place-items: center; /* centro vertical y horizontal */
}
.place-bottom {
  align-items: end; /* abajo */
  justify-items: center; /* centrado horizontal */
}
.place-top {
  align-items: start; /* arriba */
  justify-items: center;
}

/* Panel base */
.fo-panel {
  width: 100%;
  border-radius: 14px;
}

/* MODAL (centrado/variantes) */
.as-modal {
  max-width: 640px;
}

.fo-backdrop.place-bottom .as-modal {
  width: 100%;
  max-width: none;
  border-radius: 12px 12px 0 0; /* queda lindo redondeado arriba */
}

/* Drawer derecha */
.as-drawer {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  max-height: 100vh;
  width: var(--drawer-w, 520px);
  border-radius: 0;
  overflow: auto;
  background: #fff; /* asegurar fondo sólido */
}

/* Transiciones */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.2s ease, opacity 0.2s ease;
}
.slide-right-enter-from,
.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Para modal colocado abajo: desliza hacia arriba */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(16px);
  opacity: 0;
}

@media (max-width: 576px) {
  .fo-backdrop {
    padding: 8px;
  }

  .fo-panel.as-modal {
    height: calc(100dvh - 16px);
    max-height: calc(100dvh - 16px);

    display: flex;
    flex-direction: column;

    overflow: hidden;
  }

  .fo-panel.as-modal > .card-header {
    flex: 0 0 auto;
  }

  .fo-panel.as-modal > .card-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .fo-panel.as-modal > .card-footer {
    flex: 0 0 auto;

    display: grid !important;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;

    padding: 10px 12px;
    padding-bottom: calc(10px + env(safe-area-inset-bottom));

    background: #fff;
  }

  .fo-panel.as-modal > .card-footer > div {
    display: contents !important;
  }

  .fo-panel.as-modal > .card-footer .btn {
    width: 100%;
    min-width: 0;
    margin: 0 !important;
    white-space: normal;
  }
}
</style>
